
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import AmountDisplayerFilterable from '@/components/reports-v3/components/filterables/AmountDisplayerFilterable.vue';
import moment from 'moment';
import FilterWidget from '../shared/FilterWidget.vue';
import { PermissionsGroup } from '@/store/models.def';
import { roundToFixed } from '@/util/number';
import gdbx from '@/store/modules/gdbx';
import { COL, sqlSum, sqlSumByDocType, getTableName } from '@/worker/db/sqlHelpers';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import settingx from '@/store/modules/settingx';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';

@Component({
	components: {
        IncludeDebitNoteToggle,
		AmountDisplayerFilterable,
		IncludeCreditNoteToggle,
        SlPhModuleOnlyToggle,
	},
})
export default class AgentYtdRevenueContributionNumberDisplayer extends FilterWidget {
	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = [];

	public salesPercentage = '' || 0;

	public get includeCreditNote() {
		return settingx.now.includeCreditNote;
	}

    public get includeDebitNote() {
        return settingx.now.includeDebitNote;
    }

    public get slPhModuleOnly() {
        return settingx.now.slPhModuleOnly;
    }

	public get permissionIds(): PermissionsGroup[] {
		return ['customers', 'sales'];
	}

	public get currentYear() {
		return moment(this.selectedAsOfDate).year();
	}

	public get currency() {
		return gdbx.currencySymbol;
	}

	public get ytdRange(): [number, number] {
		return [
			moment(this.selectedAsOfDate).startOf('year').valueOf(),
			moment(this.selectedAsOfDate).endOf('month').valueOf(),
		];
	}

	public get decimal() {
		return gdbx.numDecimal;
	}

	public get expensiveHook() {
		const {
			selectedDateRange,
			selectedAsOfDate,
			selectedAgents,
			selectedStockItems,
			currentYear,
			includeCreditNote,
            includeDebitNote,
            slPhModuleOnly,
		} = this;
		return JSON.stringify([
			selectedDateRange,
			selectedAsOfDate,
			selectedAgents,
			selectedStockItems,
			currentYear,
			includeCreditNote,
            includeDebitNote,
            slPhModuleOnly,
		]);
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;

		const queryHead = await this._ensureTablesLoaded(
			['sales', 'creditNotes', 'customerTrades'] as any[],
		);
		this.checkInterrupted(jobId);

		const salesTable = getTableName('sales');
		const cnTable = getTableName('creditNotes');
		const arTable = getTableName('customerTrades');

		const dateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${this.ytdRange[0]} AND ${this.ytdRange[1]}`;
		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		const agentFilter = this.selectedAgents && this.selectedAgents.length > 0
			? ` AND ${COL.AGENT} IN (${this.selectedAgents.map((a: string) => `'${escapeStr(a)}'`).join(', ')})`
			: '';

		let totalSalesAmount = 0;
		let agentSalesAmount = 0;

		if (this.slPhModuleOnly) {
			const salesDocTypes = this.includeDebitNote ? ['IV', 'CS', 'DN'] : ['IV', 'CS'];
			const docTypeFilter = ` AND ${COL.DOCTYPE} IN (${salesDocTypes.map((t) => `'${t}'`).join(', ')})`;

			const sumQuery = (table: string, filter: string) => `
				SELECT ${sqlSum(COL.AMOUNT, 'totalAmt')}
				FROM ${table}
				WHERE ${dateFilter}${filter}
			`;

			const [totalSalesRows, totalCNRows, agentSalesRows, agentCNRows] = await Promise.all([
				queryHead.rawQuery(sumQuery(salesTable, docTypeFilter)),
				queryHead.rawQuery(sumQuery(cnTable, '')),
				queryHead.rawQuery(sumQuery(salesTable, `${agentFilter}${docTypeFilter}`)),
				queryHead.rawQuery(sumQuery(cnTable, agentFilter)),
			]);
			this.checkInterrupted(jobId);

			const slTotalSales = totalSalesRows[0] ? Number(totalSalesRows[0].totalAmt) || 0 : 0;
			const slTotalCN = totalCNRows[0] ? Number(totalCNRows[0].totalAmt) || 0 : 0;
			const slAgentSales = agentSalesRows[0] ? Number(agentSalesRows[0].totalAmt) || 0 : 0;
			const slAgentCN = agentCNRows[0] ? Number(agentCNRows[0].totalAmt) || 0 : 0;

			totalSalesAmount = slTotalSales - slTotalCN;
			agentSalesAmount = slAgentSales - (this.includeCreditNote ? slAgentCN : 0);
		} else {
			const sumByDocTypeQuery = (filter: string) => `
				SELECT
					${sqlSumByDocType(this.includeDebitNote ? ['IV', 'DN'] : ['IV'], COL.LOCALAMOUNT, 'iv_amount')},
					${sqlSumByDocType(['CN'], COL.LOCALAMOUNT, 'cn_amount')}
				FROM ${arTable}
				WHERE ${dateFilter}${filter}
			`;

			const [totalRows, agentRows] = await Promise.all([
				queryHead.rawQuery(sumByDocTypeQuery('')),
				queryHead.rawQuery(sumByDocTypeQuery(agentFilter)),
			]);
			this.checkInterrupted(jobId);

			const totalIv = totalRows[0] ? Number(totalRows[0].iv_amount) || 0 : 0;
			const totalCN = totalRows[0] ? Number(totalRows[0].cn_amount) || 0 : 0;
			const agentIv = agentRows[0] ? Number(agentRows[0].iv_amount) || 0 : 0;
			const agentCN = agentRows[0] ? Number(agentRows[0].cn_amount) || 0 : 0;

			totalSalesAmount = totalIv - totalCN;
			agentSalesAmount = agentIv - (this.includeCreditNote ? agentCN : 0);
		}

		this.salesPercentage = roundToFixed(
			(agentSalesAmount / totalSalesAmount) * 100,
			this.decimal,
		);

		this.saveHistory('salesPercentage');
	}
}
